import React, { useCallback, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";

import useMutation from "../../../hooks/useMutation";
import useQuery from "../../../hooks/useQuery";
import {
  DELETE_INVENTORY_ITEM_MUTATION,
  ADD_INVENTORY_ITEM_MUTATION,
} from "../../../constants/graphql/mutations";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import CartItemQuantity from "./CartItemQuantity";
import Dimensions from "./Dimensions";
import ItemId from "src/views/shopping/Orders/ItemId";
import { getImage } from "../../shopping/Cart/CartItemFields";
import { getInventoryFields } from "./CartItemFields";
import CardImage from "../../../components/card/CardImage";
import DetailRow from "../../../components/card/DetailRow";

export default function CartItemMobileCard({
  inventoryItem,
  display,
  asset,
  reFetch,
  removeInventoryItem,
  setFieldValue,
  inventoryItemsCount,
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInventoryItem] = useMutation(DELETE_INVENTORY_ITEM_MUTATION);
  const [addInventoryItem] = useMutation(ADD_INVENTORY_ITEM_MUTATION);
  const query = useQuery();
  const photo = useMemo(() => getImage(asset), [asset]);
  const index = inventoryItem.index;
  const inventoryRequestId = query.get("inventoryRequestId");
  const isRequiredCompanion = inventoryItem.requiredCompanion;
  const locked = disabled || isRequiredCompanion;

  const handleDelete = useCallback(
    async id => {
      try {
        setDeleteLoading(true);
        await deleteInventoryItem({
          variables: { id },
          onSuccess: async () => {
            removeInventoryItem(id);
            if (inventoryItemsCount === 1) {
              setFieldValue("location", inventoryItem.location);
            }
            if (typeof reFetch === "function") reFetch();
          },
        });
      } catch (error) {
        console.error("Failed to delete inventory item:", error);
      } finally {
        setDeleteLoading(false);
      }
    },
    [
      deleteInventoryItem,
      removeInventoryItem,
      setFieldValue,
      inventoryItem.location,
      inventoryItemsCount,
      reFetch,
    ]
  );

  const handleCreateNewWithQuantity = async newQuantity => {
    setLoading(true);
    try {
      await deleteInventoryItem({ variables: { id: inventoryItem.id } });
      const fields = getInventoryFields(inventoryItem);
      await addInventoryItem({
        variables: {
          ...fields,
          quantity: newQuantity,
          inventoryRequestId,
        },
        onSuccess: data => {
          const newInventoryItem = data.inventoryItem;
          setFieldValue(`inventoryItems[${index}]`, newInventoryItem);
          setLoading(false);
        },
      });
    } catch (error) {
      console.error("Failed to update inventory item:", error);
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 1.5 }}>
      {loading && (
        <Box mt={-0.5}>
          <LinearProgress sx={{ height: "2px" }} />
        </Box>
      )}
      <CardContent sx={{ "&:last-child": { pb: 2 }, position: "relative" }}>
        {display.ii_image && <CardImage image={photo} />}
        <Box display="flex" flexDirection="column" gap={1}>
          {display.ii_item_id && (
            <DetailRow label="Item ID">
              <Box display="flex" flexDirection="column">
                <Typography variant="body2" fontWeight={500}>
                  <ItemId asset={asset} />
                </Typography>
                {isRequiredCompanion && (
                  <Typography variant="caption" color="text.secondary">
                    Required companion
                  </Typography>
                )}
              </Box>
            </DetailRow>
          )}
          {display.ii_sku_number && asset.skuNumber && (
            <DetailRow label="Article&nbsp;#">
              <Typography variant="body2" noWrap>
                {asset.skuNumber}
              </Typography>
            </DetailRow>
          )}
          {display.ii_barcode && asset.barcode && (
            <DetailRow label="Barcode" value={asset.barcode} />
          )}
          {display.ii_model && asset.model && <DetailRow label="Model" value={asset.model} />}
          {display.ii_dimensions && Array.isArray(asset.dimensions) && asset.dimensions.length > 0 && (
            <DetailRow label="Dimensions (W x D x H)">
              <Dimensions dimensions={asset.dimensions} />
            </DetailRow>
          )}
          {asset?.description && (
            <DetailRow label="Description">
              <Typography variant="body2" noWrap>
                {asset.description}
              </Typography>
            </DetailRow>
          )}

          {(display.ii_qtyRequested || !disabled) && (
            <>
              <Box my={0.5}>
                <Divider />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                {display.ii_qtyRequested ? (
                  <CartItemQuantity
                    disabled={locked || asset.type === "Kit" || inventoryItem.pickedStatus}
                    value={inventoryItem.quantity || ""}
                    availableQuantity={asset.availableQuantity}
                    onChange={quantity => handleCreateNewWithQuantity(quantity)}
                  />
                ) : (
                  <div />
                )}
                {!locked ? (
                  <IconButton disabled={deleteLoading} onClick={() => setShowDeleteModal(true)}>
                    {deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
                  </IconButton>
                ) : (
                  <div />
                )}
              </Box>
            </>
          )}
        </Box>

        {showDeleteModal && (
          <ConfirmDeleteModal
            title="Remove Item"
            applyLabel="Remove"
            descr="Are you sure you want to remove the selected item from your order?"
            onClose={() => setShowDeleteModal(false)}
            onDelete={() => handleDelete(inventoryItem.id)}
          />
        )}
      </CardContent>
    </Card>
  );
}
